import type { ErrorObject } from "ajv";
import type { SchemaProblem } from "./public";

/**
 * ajv 오류 목록을 사람이 읽는 문제 목록으로 바꾼다. 각 문제는 점 경로 · ajv 원문(영어) · 한국어 문장을 가진다.
 *
 * nullable 칸(`anyOf: [X, {type: "null"}]`)이 틀리면 ajv 는 세 오류를 낸다. X 가지의 오류, null 가지의
 * `type` 오류, `anyOf` 오류다. 사람에게는 하나로 보인다. 그래서 null 가지와 `anyOf` 오류를 지우고, 같은 경로의
 * X 오류 문장에 "또는 null" 을 더한다. 값이 객체라 X 안쪽에서 틀렸으면 안쪽 오류만 남긴다. `if` 오류는
 * then 가지의 오류가 함께 오므로 지운다. 지울 오류만 있고 남는 오류가 없으면 지우지 않는다.
 *
 * 조건 규칙(`then` · `else`)이 요구하는 null 은 nullable 칸의 null 가지가 아니다. 그 `type` 오류는 실제
 * 위반이라 남긴다. 예) `kind` 가 `absolute` 인데 `delta_frame` 을 적었다.
 */
export function describeSchemaErrors(errors: readonly ErrorObject[]): SchemaProblem[] {
  const entries = errors.map((error) => ({ error, path: toDotPath(error) }));
  const nullablePaths = new Set(
    entries
      .filter(({ error }) => isNullBranch(error))
      .map(({ error }) => pointerToDotPath(error.instancePath)),
  );
  const kept = entries.filter(({ error }) => !isNoise(error, nullablePaths));
  const shown = kept.length > 0 ? kept : entries;
  return shown.map(({ error, path }) => ({
    path,
    message: error.message ?? error.keyword,
    text: describe(error, nullablePaths.has(pointerToDotPath(error.instancePath))),
  }));
}

const NULL_BRANCH = /\/anyOf\/\d+\/type$/;

/** nullable 칸(`anyOf: [X, {type: "null"}]`)의 null 가지에서 난 `type` 오류인지. */
function isNullBranch(error: ErrorObject): boolean {
  return (
    error.keyword === "type" && error.params.type === "null" && NULL_BRANCH.test(error.schemaPath)
  );
}

function isNoise(error: ErrorObject, nullablePaths: ReadonlySet<string>): boolean {
  switch (error.keyword) {
    case "type":
      return isNullBranch(error);
    case "anyOf":
      return nullablePaths.has(pointerToDotPath(error.instancePath));
    case "if":
      return true;
    default:
      return false;
  }
}

/**
 * 뜻을 아는 `pattern` 의 문장. 정본 스키마가 쓰는 패턴이다. 모르는 패턴은 원문을 보인다.
 * 첫 줄은 공백뿐인 문자열과 NUL 문자를 막는 패턴, 둘째 줄은 이름(영문 · 숫자 · `_`) 패턴이다.
 */
const PATTERN_TEXT: Readonly<Record<string, string>> = {
  "^[^\\S\\x00]*[^\\s\\x00][^\\x00]*$": "빈 칸만 있는 문자열과 NUL 문자는 쓸 수 없습니다",
  "^[A-Za-z0-9_]+$": "영문 · 숫자 · _ 만 쓸 수 있습니다",
};

const TYPE_NAME: Readonly<Record<string, string>> = {
  string: "문자열",
  number: "숫자",
  integer: "정수",
  boolean: "true · false",
  object: "객체",
  array: "배열",
  null: "null",
};

function describe(error: ErrorObject, nullable: boolean): string {
  const params: Record<string, unknown> = error.params;
  const orNull = nullable ? " 또는 null" : "";
  switch (error.keyword) {
    case "required":
      return `"${String(params.missingProperty)}" 키가 없습니다. 값이 없어도 키는 적어야 합니다.`;
    case "additionalProperties":
      return `"${String(params.additionalProperty)}" 는 스키마에 없는 키입니다.`;
    case "type":
      return `${String(params.type)
        .split(",")
        .map((name) => TYPE_NAME[name] ?? name)
        .join(" 또는 ")}${orNull} 값이어야 합니다.`;
    case "enum": {
      const allowed = Array.isArray(params.allowedValues) ? params.allowedValues : [];
      const values = [
        ...allowed.map((value) => JSON.stringify(value)),
        ...(nullable ? ["null"] : []),
      ];
      return `${values.join(" · ")} 중 하나여야 합니다.`;
    }
    case "const":
      return `값은 ${JSON.stringify(params.allowedValue)}${orNull} 이어야 합니다.`;
    case "minItems":
      return `항목이 ${String(params.limit)}개 이상이어야 합니다.`;
    case "maxItems":
      return `항목이 ${String(params.limit)}개 이하여야 합니다.`;
    case "minimum":
      return `${String(params.limit)} 이상이어야 합니다.`;
    case "maximum":
      return `${String(params.limit)} 이하여야 합니다.`;
    case "exclusiveMinimum":
      return `${String(params.limit)} 보다 커야 합니다.`;
    case "maxLength":
      return `${String(params.limit)}자 이하여야 합니다.`;
    case "pattern": {
      const known = PATTERN_TEXT[String(params.pattern)];
      return known === undefined
        ? `"${String(params.pattern)}" 형식에 맞아야 합니다.`
        : `${known}${nullable ? ". 값이 없으면 null 로 적어 주세요" : ""}.`;
    }
    default:
      return `스키마 규칙(${error.keyword})에 맞지 않습니다.`;
  }
}

/** 빠진 키와 모르는 키는 그 이름까지 붙인 점 경로다. */
function toDotPath(error: ErrorObject): string {
  const params: Record<string, unknown> = error.params;
  const key = params.missingProperty ?? params.additionalProperty;
  const base = pointerToDotPath(error.instancePath);
  if (typeof key !== "string") {
    return base;
  }
  return base === "" ? key : `${base}.${key}`;
}

/** JSON Pointer(`/hands/0`)를 점 경로(`hands.0`)로 바꾼다. */
function pointerToDotPath(pointer: string): string {
  return pointer
    .split("/")
    .slice(1)
    .map((segment) => segment.replace(/~1/g, "/").replace(/~0/g, "~"))
    .join(".");
}
